import Keycloak from 'keycloak-js';
import { writable, type Readable } from 'svelte/store';
import { resolveKeycloakConfig } from './keycloak-config';

export type AuthStatus = 'initializing' | 'authenticated' | 'error';

export interface AuthState {
  status: AuthStatus;
  username?: string;
  keycloak?: Keycloak;
}

/**
 * Number of seconds of remaining token validity below which the token is
 * proactively refreshed. Matches the guard used by the legacy webapp.
 */
const MIN_TOKEN_VALIDITY_SECONDS = 30;

const authStateStore = writable<AuthState>({ status: 'initializing' });

/** Reactive authentication state for the shell. */
export const authState: Readable<AuthState> = authStateStore;

let initPromise: Promise<void> | undefined;
let keycloak: Keycloak | undefined;

/**
 * Initializes the Keycloak public UI client and performs the `login-required`
 * flow, redirecting to Keycloak when the user has no session — mirroring the
 * legacy webapp's `login-required.mustache`.
 *
 * Idempotent: repeated calls return the same in-flight/settled promise so the
 * flow runs at most once per page load.
 */
export function initAuth(hostname: string): Promise<void> {
  if (initPromise) {
    return initPromise;
  }

  initPromise = runInit(hostname);
  return initPromise;
}

async function runInit(hostname: string): Promise<void> {
  const config = resolveKeycloakConfig(hostname);
  if (!config) {
    authStateStore.set({ status: 'error' });
    return;
  }

  const instance = new Keycloak(config);
  keycloak = instance;

  instance.onTokenExpired = () => {
    instance.updateToken(MIN_TOKEN_VALIDITY_SECONDS).catch(() => {
      instance.login();
    });
  };

  try {
    await instance.init({
      onLoad: 'login-required',
      checkLoginIframe: false,
      pkceMethod: 'S256'
    });
  } catch {
    authStateStore.set({ status: 'error' });
    return;
  }

  if (!instance.authenticated) {
    authStateStore.set({ status: 'error' });
    return;
  }

  authStateStore.set({
    status: 'authenticated',
    username: resolveUsername(instance),
    keycloak: instance
  });
}

function resolveUsername(keycloak: Keycloak): string | undefined {
  const token = keycloak.tokenParsed as
    { preferred_username?: string; name?: string; email?: string } | undefined;
  return token?.preferred_username ?? token?.name ?? token?.email;
}

/**
 * Returns a currently-valid raw access token, refreshing it first if it is
 * within {@link MIN_TOKEN_VALIDITY_SECONDS} of expiry.
 *
 * Returns `null` when Keycloak has not been initialized or the user is not
 * authenticated. A failed refresh is swallowed and the existing token (if any)
 * is returned, since callers surface their own auth errors.
 */
export async function getAccessToken(): Promise<string | null> {
  if (!keycloak || !keycloak.authenticated) {
    return null;
  }

  try {
    await keycloak.updateToken(MIN_TOKEN_VALIDITY_SECONDS);
  } catch {
    // Refresh failed; fall through and return whatever token we still hold.
  }

  return keycloak.token ?? null;
}
